import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { ButtonWithIcon } from '@els/els-react--button';
import { Loader } from '@els/els-react--loader';
import history from '@AppRoutes/history';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import StructureEditor from '@Containers/StructureEditor';
import StructureEditorType from '@Containers/StructureEditor/constants';
import useUserSettings from '@Hooks/useUserSettings';
import EditorSwitcher from '@Pages/RetroSynthesis/DrawStructure/EditorSwitcher';
import GSFN from '@Pages/RetroSynthesis/DrawStructure/GSFN';
import { paths } from '@AppRoutes/constants.js';
import { createProjectFromDrawnReaction } from './ConditionsService';
import { goToCreatedConditionsProject } from './navigation';
import { trackDrawInsertStructure, trackDrawClear, trackDrawCancel, trackDrawSearchConditions } from './analytics';
import './ConditionsDraw.scss';

let tempData;

function isHandledByGenericErrorFlow(error) {
  return Boolean(error?.status || error?.statusCode || error?.component);
}

function ConditionsDraw() {
  const location = useLocation();
  const { referenceLabel, initialStructure, noProjects } = location.state || {};
  const editorRef = useRef(null);
  const [editorType, setEditorType] = useState(null);
  const [isEmptyEditor, setIsEmptyEditor] = useState(true);
  const [isEditorLoading, setIsEditorLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  useUserSettings((userSettings) => {
    if (editorType == null) {
      setEditorType(StructureEditorType.getFromSetting(userSettings.structureEditorPreselected) || StructureEditorType.MARVIN);
    }
  });

  useEffect(() => {
    if (initialStructure) {
      tempData = initialStructure;
    }
  }, [initialStructure]);

  const insertStructureHandler = (structure) => {
    try {
      const instance = editorRef?.current?.instance;
      instance?.clear?.();
      instance?.loadStructure?.(structure);
    } catch (error) {
      Logger.warn('ConditionsDraw insert structure failed with error:', error);
    }
  };

  const cancelHandler = () => {
    trackDrawCancel();
    history.push(paths.CONDITIONS);
  };

  const clearHandler = () => {
    trackDrawClear();
    try {
      editorRef?.current?.instance?.clear?.();
      setIsEmptyEditor(true);
    } catch (error) {
      Logger.warn('ConditionsDraw clear editor failed with error:', error);
    }
  };

  const editorTypeChangeHandler = async (newEditorType) => {
    setIsEditorLoading(true);
    const instance = editorRef?.current?.instance;
    if (instance) {
      try {
        const isEditorEmpty = await instance?.isEmpty?.();
        if (!isEditorEmpty) {
          const result = await instance?.getData?.();
          tempData = result?.data;
        }
      } catch (error) {
        Logger.warn('ConditionsDraw transfer data when switching editor failed with error:', error);
      }
    }
    setEditorType(newEditorType);
  };

  const loadedEditorHandler = async () => {
    // tempData is set when switching editor types and when arriving from the Edit button.
    if (tempData) {
      try {
        await editorRef?.current?.instance?.loadStructure?.(tempData);
        tempData = undefined;
      } catch {
        // restore failed — proceed without previous structure
        tempData = undefined;
      }
    }
    setIsEditorLoading(false);
  };

  const handleEditorChange = (isEmpty) => {
    setIsEmptyEditor(isEmpty);
  };

  const searchHandler = async () => {
    const instance = editorRef?.current?.instance;
    if (!instance || isSearching || isEditorLoading) return;

    trackDrawSearchConditions();
    setIsSearching(true);
    try {
      const { data } = await instance.getData();
      const project = await createProjectFromDrawnReaction({ reaction: data });
      // react-router v5 under a React 18 concurrent root can defer the first
      // programmatic history.push fired from an async callback, which leaves
      // the blocking loader visible. Force the router update to commit now.
      flushSync(() => {
        goToCreatedConditionsProject(history, project, { preferProjectsPage: true });
      });
    } catch (error) {
      Logger.warn('ConditionsDraw search failed with error:', error);
      if (!isHandledByGenericErrorFlow(error)) {
        NotifyDialogsService.openModalWindow({
          type: 'error',
          title: 'Condition search failed',
          text: error?.message || 'Condition search could not be created.',
          confirmText: 'Close',
        });
      }
      setIsSearching(false);
    }
  };

  return (
    <div className="conditions-draw draw-structure">
      <Loader isVisible={isSearching} isBlocking />
      {referenceLabel && (
        <div className="conditions-draw__reference-banner">
          <span className="u-els-color-n5 u-els-font-size-meta">From: </span>
          <span className="u-els-font-size-meta u-els-font-weight-bold">{referenceLabel}</span>
        </div>
      )}
      {/* SDD §8.4 / WCAG 2.4.1: id="main-content" on the <main> landmark so skip links
          land on the landmark itself, not on the anonymous wrapper div. */}
      <main
        id="main-content"
        className="conditions-draw__workspace rx-left-gutter rx-right-gutter"
        aria-label="Condition search structure editor">
        <div className="conditions-draw__editor">
          <div className="conditions-draw__top-panel draw-structure__top-panel u-els-padding-bottom">
            <EditorSwitcher
              editorType={editorType}
              onChange={editorTypeChangeHandler}
              isLoading={isEditorLoading}
              className="conditions-draw__editor-switcher"
            />
            <GSFN onStructureLoaded={insertStructureHandler} onButtonClick={trackDrawInsertStructure} />
          </div>
          <StructureEditor
            onChange={handleEditorChange}
            editorType={editorType}
            editorInstanceRef={editorRef}
            className="conditions-draw__structure-editor"
            onLoaded={loadedEditorHandler}
          />
          <section className="conditions-draw__editor-controls" aria-label="Structure editor actions">
            <ButtonWithIcon
              data-e2e="conditions-draw-clear"
              type="borderless"
              size="small"
              sprite="Trash"
              linkIconSize="s"
              iconRight
              onClick={clearHandler}
              disabled={isEmptyEditor}>
              Clear
            </ButtonWithIcon>
            <ButtonWithIcon
              data-e2e="conditions-draw-cancel"
              type="tertiary"
              size="small"
              sprite="Close"
              linkIconSize="s"
              iconRight
              onClick={cancelHandler}
              disabled={isSearching || (noProjects && isEmptyEditor)}>
              Cancel
            </ButtonWithIcon>
            <ButtonWithIcon
              data-e2e="conditions-draw-search"
              type="primary"
              size="small"
              sprite="ChevronRight"
              linkIconSize="s"
              iconRight
              onClick={searchHandler}
              disabled={isEmptyEditor || isEditorLoading || isSearching}>
              Search conditions
            </ButtonWithIcon>
          </section>
        </div>
      </main>
    </div>
  );
}

export default ConditionsDraw;
